import { useCallback } from 'react';

export interface PickedImage {
  uri: string;
  name: string;
  type: string;
  width: number;
  height: number;
  file?: File; // 웹 picker가 보존한 File 객체 (RN은 undefined)
}

interface GetImagesParams {
  currentSize: number;
  maxPhotoSize?: number;
}

const pickFiles = ({
  multiple,
  capture,
}: {
  multiple?: boolean;
  capture?: 'environment' | 'user';
}): Promise<File[]> =>
  new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (multiple === true) input.multiple = true;
    if (capture != null) input.capture = capture;
    input.onchange = () => resolve(Array.from(input.files ?? []));
    input.oncancel = () => resolve([]);
    input.click();
  });

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const toPickedImage = async (file: File): Promise<PickedImage> => ({
  uri: await fileToDataUrl(file),
  name: file.name,
  type: file.type || 'image/jpeg',
  width: 0,
  height: 0,
  file,
});

export const useImagePicker = () => {
  const takePhoto = useCallback(async (): Promise<PickedImage[]> => {
    const files = await pickFiles({ capture: 'environment' });
    return Promise.all(files.map(toPickedImage));
  }, []);

  const getImages = useCallback(
    async ({
      currentSize,
      maxPhotoSize,
    }: GetImagesParams): Promise<PickedImage[]> => {
      const remaining =
        maxPhotoSize != null ? maxPhotoSize - currentSize : undefined;
      const files = await pickFiles({
        multiple: maxPhotoSize == null || maxPhotoSize > 1,
      });
      const limited = remaining != null ? files.slice(0, remaining) : files;
      return Promise.all(limited.map(toPickedImage));
    },
    [],
  );

  return { getImages, takePhoto };
};
